// Material presets built from tokens.
//
// Each preset is one CSSProperties object spread at call sites. The
// *material* (fill, blur, shadow, bevel) resolves through CSS variables
// so the active theme decides the substance:
//
//   glass     — translucent white fill + real backdrop blur + hairline
//               edge + top bevel + restrained dark drop shadow.
//   earth /   — opaque fills, blur disabled (--blur-* → none), no
//   midnight    elevation shadow. Same geometry, flat-solid substance.
//
// Because every colour/material value is a var(...), call sites never
// change between themes — only the theme definitions in
// src/theme/themes/*.ts do.
//
// Five sizes:
//   glassPanel  — cards, heroes, the biggest surfaces
//   glassTile   — mid-sized tiles (agent control, dashboard cards)
//   glassKpi    — row-level tiles nested inside a panel
//   glassInset  — sunken slot "carved into" a larger glass surface
//   glassButton — small ghost-glass button chrome

import type { CSSProperties } from "react";
import { colors, effects, radius } from "./tokens";

// ─── hairline border + bevel helpers ───
//
// We model the "1px border + top highlight" as inset box-shadows
// rather than CSS border so borders don't consume layout. Every
// preset composes these in the same order:
//   1. inset 0 0 0 1px hairline   — the full-perimeter edge
//   2. inset 0 1px 0 bevel        — the top-edge light catch
//   3. var(--shadow-*)            — the outside drop shadow

const HAIRLINE = `inset 0 0 0 1px ${colors.borderHairline}`;
const BEVEL_TOP = `inset 0 1px 0 ${colors.bevelTop}`;

// ─── glass presets ───

/** Largest surfaces — page panels, heroes, dashboard cards. */
export const glassPanel: CSSProperties = {
  background: colors.glassPanel,
  backdropFilter: "var(--blur-lg)",
  WebkitBackdropFilter: "var(--blur-lg)",
  borderRadius: radius.xxl, // 16px
  boxShadow: [HAIRLINE, BEVEL_TOP, effects.panelLift].join(", "),
};

/** Mid-sized tiles — agent control, dashboard cards. */
export const glassTile: CSSProperties = {
  background: colors.glassTile,
  backdropFilter: "var(--blur-lg)",
  WebkitBackdropFilter: "var(--blur-lg)",
  borderRadius: radius.xxl,
  boxShadow: [HAIRLINE, BEVEL_TOP, effects.tileLift].join(", "),
};

/** Row-level tiles. Used inside a larger glass panel as a nested
 * list item. Lighter blur so a panel's own blur doesn't compound. */
export const glassKpi: CSSProperties = {
  background: colors.glassKpi,
  backdropFilter: "var(--blur-md)",
  WebkitBackdropFilter: "var(--blur-md)",
  borderRadius: radius.xl, // 12px
  boxShadow: [HAIRLINE, BEVEL_TOP].join(", "),
};

/** Sunken slot — "carved into" a larger glass surface. Darker
 * translucent fill, no bevel (the inset shadow carries the
 * "recessed" read instead). */
export const glassInset: CSSProperties = {
  background: colors.glassInset,
  borderRadius: radius.lg,
  boxShadow: [
    `inset 0 1px 2px ${colors.bevelBottomDark}`,
    `inset 0 0 0 1px ${colors.borderHairline}`,
  ].join(", "),
};

/** Small inset button — the ghost-glass buttons used for Chat /
 * Desktop / cog / every other in-tile affordance. The hover state is
 * a separate export so components can snap to it on onMouseEnter. */
export const glassButton: CSSProperties = {
  background: colors.glassButton,
  backdropFilter: "var(--blur-sm)",
  WebkitBackdropFilter: "var(--blur-sm)",
  borderRadius: radius.xl,
  boxShadow: HAIRLINE,
};

export const glassButtonHover: CSSProperties = {
  background: colors.glassButtonHover,
  backdropFilter: "var(--blur-sm)",
  WebkitBackdropFilter: "var(--blur-sm)",
  borderRadius: radius.xl,
  boxShadow: `inset 0 0 0 1px ${colors.borderSubtle}`,
};

/**
 * Primary button — accent-tinted, used for the dashboard's "Chat with
 * Julia" and any other primary action. Tinted via --accent-rgb so it
 * tracks the theme's accent (steel-blue in glass, terracotta in earth).
 */
export const glassButtonPrimary: CSSProperties = {
  background: "rgba(var(--accent-rgb), 0.18)",
  backdropFilter: "var(--blur-sm)",
  WebkitBackdropFilter: "var(--blur-sm)",
  borderRadius: radius.xl,
  boxShadow: "inset 0 0 0 1px rgba(var(--accent-rgb), 0.25)",
};

export const glassButtonPrimaryHover: CSSProperties = {
  background: "rgba(var(--accent-rgb), 0.28)",
  backdropFilter: "var(--blur-sm)",
  WebkitBackdropFilter: "var(--blur-sm)",
  borderRadius: radius.xl,
  boxShadow: "inset 0 0 0 1px rgba(var(--accent-rgb), 0.32)",
};

/**
 * The "active selection" glass — used on the sidebar's active nav row
 * and on selected-state surfaces. Accent tint with a slightly stronger
 * border so it reads as "this is the one you're on".
 */
export const glassActive: CSSProperties = {
  background: colors.glassActiveAccent,
  boxShadow: [
    `inset 0 0 0 1px ${colors.glassActiveAccentBorder}`,
    `inset 0 1px 0 ${colors.bevelTop}`,
  ].join(", "),
};

// ─── tab strip presets ───
//
// Every tabbed pill strip in the app shares this pattern: sunken
// container, transparent inactive buttons, translucent active button.

/** Container that wraps a row of tab buttons. Sunken tray look. */
export const tabStripContainer: CSSProperties = {
  display: "flex",
  gap: "4px",
  padding: "4px",
  background: "rgba(0, 0, 0, 0.2)",
  borderRadius: radius.lg,
  boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.3)",
};

/** Inactive tab — transparent, faint text (the AA faint token). */
export const tabItemInactive: CSSProperties = {
  background: "transparent",
  color: colors.textFaint,
  borderRadius: radius.lg,
};

/** Active tab — ink-tinted pill, brighter text, no border. At 0.04 the
 * pill reads as a gentle depression in the surface. */
export const tabItemActive: CSSProperties = {
  background: "rgba(var(--ink-rgb), 0.04)",
  color: colors.textPrimary,
  borderRadius: radius.lg,
};

// ─── avatar square preset ───
//
// Every "J"-style agent avatar uses this material: flat accent fill at
// low opacity with a hairline border and a quiet inner highlight.

export const avatarSquare: CSSProperties = {
  background: colors.avatarBg,
  borderRadius: radius.md,
  color: colors.avatarText,
  boxShadow: [
    `inset 0 0 0 1px ${colors.avatarBorder}`,
    `inset 0 1px 0 ${colors.bevelTop}`,
  ].join(", "),
};

// ─── status badge factory ───
//
// Every tinted pill in the app (FACT, status chips, memory category
// tags, etc.) is one rule: the accent at 10% fill, 15% border, full-
// strength text (the semantic colours clear AA on their own 10% tint).
// `color-mix` is used (rather than parsing a hex) so the input can be a
// theme var(--c-*) — pass a semantic token (colors.success …), not a hex.
// Type follows the theme's label treatment (font, case, tracking,
// weight) via the --label-* vars, at the 12px floor.

export function statusBadge(accent: string): CSSProperties {
  return {
    background: `color-mix(in srgb, ${accent} 10%, transparent)`,
    color: accent,
    boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${accent} 15%, transparent)`,
    borderRadius: radius.sm,
    padding: "2px 8px",
    fontSize: "12px",
    lineHeight: "16px",
    fontFamily: "var(--label-font)",
    textTransform: "var(--label-transform)" as CSSProperties["textTransform"],
    letterSpacing: "var(--label-tracking)",
    fontWeight: "var(--label-weight)" as CSSProperties["fontWeight"],
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  };
}

/**
 * Pill-shaped member chip — the "Julia"-style team member tag.
 * Accent-tinted glass with a full-round radius. `color` overrides the
 * default accentLight if a caller wants a custom accent.
 */
export function memberPill(accentHex: string = colors.accentLight): CSSProperties {
  return {
    background: colors.glassActiveAccent,
    color: accentHex,
    boxShadow: `inset 0 0 0 1px ${colors.glassActiveAccentBorder}`,
    borderRadius: radius.pill,
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  };
}
